/* ==========================================================================
   NEFER Studio — storefront behaviour
   assets/nefer-shop.js — deferred. Companion to nefer-motion.js, which owns
   all animation. This file owns state: variants, quantity, the cart drawer
   (AJAX + Section Rendering), the Shop dropdown and the mobile buy bar.

   Contract with Liquid:
     [data-nf-variants]        JSON script tag: [{id, options[], price, available, image, title}]
     [data-nf-option-group]    one group per product option, data-name = option name
     [data-nf-option-value]    a swatch button inside a group
     [data-nf-selected]        the "· <value>" text beside the group label
     [data-nf-price]           price node(s) to refresh on variant change
     [data-nf-variant-input]   hidden input name="id" in the buy form
     [data-nf-qty]             quantity stepper wrapper (button[data-nf-step], output)
     [data-nf-qty-input]       hidden quantity input
     [data-nf-add]             an add-to-cart form (product page + buy bar)
     [data-nf-drop-toggle]     Shop dropdown button, aria-controls = panel id
     [data-nf-cart-count]      header cart count
     [data-nf-cart-total]      hidden node inside the drawer carrying cart.item_count
     [data-nf-line-step]       drawer quantity button, data-line + data-to
     [data-nf-rec-add]         drawer recommendation add button, data-id
     [data-nf-recs]            drawer recommendation slot, data-seed = product id
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var DRAWER_ID = 'nefer-cart';
  var money = (window.NEFER && window.NEFER.moneyFormat) || '{{amount}}';

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return (root || document).querySelectorAll(sel); }

  /* ------------------------------------------------------------------ *
   * Money — mirrors Shopify's money_format placeholders so JS-rendered
   * prices (recommendations, line totals) match Liquid-rendered ones.
   * ------------------------------------------------------------------ */

  function group(intStr, sep) { return intStr.replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + sep); }

  function formatMoney(cents, format) {
    var fmt = format || money;
    var value = (Number(cents) || 0) / 100;
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      var parts, whole, frac;
      switch (name) {
        case 'amount_no_decimals':
          return group(String(Math.round(value)), ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(String(Math.round(value)), '.');
        case 'amount_no_decimals_with_space_separator':
          return group(String(Math.round(value)), ' ');
        case 'amount_with_comma_separator':
          parts = value.toFixed(2).split('.');
          return group(parts[0], '.') + ',' + parts[1];
        case 'amount_with_space_separator':
          parts = value.toFixed(2).split('.');
          return group(parts[0], ' ') + ',' + parts[1];
        case 'amount_with_apostrophe_separator':
          parts = value.toFixed(2).split('.');
          return group(parts[0], "'") + '.' + parts[1];
        default: /* amount */
          parts = value.toFixed(2).split('.');
          whole = group(parts[0], ',');
          frac = parts[1];
          return whole + '.' + frac;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 1. Variant selection
   *    Reads the product's EXISTING options — never invents, renames or
   *    reorders values (README §0 rule 3, §6.3).
   * ------------------------------------------------------------------ */

  function initVariants(scope) {
    each($$('[data-nf-product]', scope || document), function (root) {
      var tag = $('[data-nf-variants]', root);
      if (!tag) return;

      var variants;
      try { variants = JSON.parse(tag.textContent); } catch (e) { return; }
      if (!variants || !variants.length) return;

      var groups = $$('[data-nf-option-group]', root);

      function selection() {
        return Array.prototype.map.call(groups, function (g) {
          var on = $('[data-nf-option-value][aria-checked="true"]', g);
          return on ? on.getAttribute('data-nf-option-value') : null;
        });
      }

      function match(sel) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i], ok = true;
          for (var k = 0; k < sel.length; k++) {
            if (sel[k] !== null && v.options[k] !== sel[k]) { ok = false; break; }
          }
          if (ok) return v;
        }
        return null;
      }

      function apply(v) {
        if (!v) return;

        each($$('[data-nf-variant-input]', root), function (i) { i.value = v.id; });
        each($$('[data-nf-price]', root), function (p) { p.textContent = formatMoney(v.price); });

        /* Stock state follows the variant. A piece on the shelf ships in days;
           anything else is a preorder at the full lead time. Untracked variants
           fall to the preorder side — with no count there is nothing to promise.
           The button label moves with it, so the customer is never told "Add to
           cart" for something that has not been made yet. */
        var inStock = !!(v.tracked && v.inv > 0);

        each($$('[data-nf-stock]', root), function (box) {
          var state = $('[data-nf-stock-state]', box);
          var ship = $('[data-nf-stock-ship]', box);
          if (state) {
            state.classList.toggle('is-in', inStock);
            state.textContent = inStock
              ? box.getAttribute('data-nf-in-label') + ' · ' + v.inv + ' available'
              : box.getAttribute('data-nf-pre-label');
          }
          if (ship) {
            ship.textContent = inStock
              ? box.getAttribute('data-nf-ship-stock')
              : box.getAttribute('data-nf-ship-preorder');
          }
        });

        each($$('[data-nf-add-btn]', root), function (btn) {
          btn.textContent = inStock
            ? btn.getAttribute('data-nf-label-stock')
            : btn.getAttribute('data-nf-label-preorder');
        });
        each(groups, function (g, k) {
          var out = $('[data-nf-selected]', g);
          if (out) out.textContent = v.options[k] || '';
        });

        /* The gallery already holds every product image; jump to this
           variant's image rather than swapping any src. */
        if (v.image_id) {
          var thumb = $('[data-nf-thumb][data-nf-image="' + v.image_id + '"]', root);
          if (thumb) thumb.click();
        }

        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }

      each(groups, function (g) {
        each($$('[data-nf-option-value]', g), function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            each($$('[data-nf-option-value]', g), function (b) { b.setAttribute('aria-checked', 'false'); });
            btn.setAttribute('aria-checked', 'true');
            apply(match(selection()));
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Quantity stepper — label above, minimal, no bulk UI
   * ------------------------------------------------------------------ */

  function initQty(scope) {
    each($$('[data-nf-qty]', scope || document), function (box) {
      var out = $('output', box);
      var root = box.closest('[data-nf-product]') || document;

      function set(n) {
        n = Math.max(1, n);
        if (out) out.textContent = String(n);
        each($$('[data-nf-qty-input]', root), function (i) { i.value = n; });
      }

      each($$('[data-nf-step]', box), function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var cur = parseInt((out && out.textContent) || '1', 10) || 1;
          set(cur + parseInt(btn.getAttribute('data-nf-step'), 10));
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Cart drawer — AJAX + Section Rendering, no page reload
   * ------------------------------------------------------------------ */

  function drawer() { return document.getElementById(DRAWER_ID); }

  function swapDrawer(html) {
    var el = drawer();
    if (!el || !html) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById(DRAWER_ID);
    if (!fresh) return;

    var open = !el.hasAttribute('hidden');
    el.innerHTML = fresh.innerHTML;
    if (open) el.removeAttribute('hidden');

    syncCount();
    bindDrawer();
    if (window.NeferMotion) window.NeferMotion.refresh(el);
  }

  function syncCount() {
    var total = $('[data-nf-cart-total]');
    if (!total) return;
    var n = total.textContent.trim();
    each($$('[data-nf-cart-count]'), function (c) { c.textContent = n; });
  }

  function post(url, body) {
    return fetch(ROOT.replace(/\/$/, '') + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function addToCart(id, quantity, onDone) {
    return post('/cart/add.js', {
      items: [{ id: Number(id), quantity: Number(quantity) || 1 }],
      sections: 'cart-drawer'
    }).then(function (data) {
      if (data && data.sections && data.sections['cart-drawer']) {
        swapDrawer(data.sections['cart-drawer']);
      }
      if (window.NeferMotion) window.NeferMotion.open(drawer());
      hideBuyBar(true);
      if (onDone) onDone(data);
    });
  }

  /* Multi-line add, for the Collections page's bundle button. One request for the
     whole set — N sequential calls race each other and the drawer flickers.
     Unlike addToCart this REJECTS on a Shopify error (cart/add.js answers 4xx
     with a JSON body, which fetch does not treat as a failure), so the caller
     can retry line by line and keep whatever is still in stock. */
  function addItems(items, onDone) {
    return post('/cart/add.js', { items: items, sections: 'cart-drawer' })
      .then(function (data) {
        if (data && data.status && Number(data.status) >= 400) throw data;
        if (data && data.sections && data.sections['cart-drawer']) {
          swapDrawer(data.sections['cart-drawer']);
        }
        if (window.NeferMotion) window.NeferMotion.open(drawer());
        hideBuyBar(true);
        if (onDone) onDone(data);
        return data;
      });
  }

  function changeLine(line, quantity) {
    return post('/cart/change.js', {
      line: Number(line),
      quantity: Number(quantity),
      sections: 'cart-drawer'
    }).then(function (data) {
      if (data && data.sections && data.sections['cart-drawer']) {
        swapDrawer(data.sections['cart-drawer']);
      }
    });
  }

  function initAddForms(scope) {
    each($$('[data-nf-add]', scope || document), function (form) {
      if (form.__nf) return;
      form.__nf = 1;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var id = $('[data-nf-variant-input]', form);
        var qty = $('[data-nf-qty-input]', form);
        var btn = form.querySelector('button[type="submit"]');
        if (!id || !id.value) return;

        var label = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

        addToCart(id.value, qty ? qty.value : 1).catch(function () {
          /* Network failure: fall back to a real form post so the customer
             is never stranded. form.submit() skips submit listeners, so this
             cannot loop back into the handler above. */
          form.submit();
        }).then(function () {
          if (btn) { btn.disabled = false; btn.textContent = label; }
        });
      });
    });
  }

  function bindDrawer() {
    var el = drawer();
    if (!el) return;

    each($$('[data-nf-line-step]', el), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        changeLine(btn.getAttribute('data-nf-line'), btn.getAttribute('data-nf-to'));
      });
    });

    each($$('[data-nf-rec-add]', el), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        btn.disabled = true;
        addToCart(btn.getAttribute('data-nf-rec-add'), 1);
      });
    });

    loadRecommendations(el);
  }

  /* Complementary recommendations, falling back to whatever the section
     was given as curated picks (which are already in the markup). */
  function loadRecommendations(el) {
    var slot = $('[data-nf-recs]', el);
    if (!slot || slot.__nf) return;
    slot.__nf = 1;

    var seed = slot.getAttribute('data-nf-seed');
    if (!seed) return;

    fetch(ROOT.replace(/\/$/, '') + '/recommendations/products.json?product_id=' + seed +
          '&limit=2&intent=complementary')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) return;
        var html = data.products.map(function (p) {
          var img = p.featured_image
            ? '<img src="' + p.featured_image + '&width=160" alt="' +
              String(p.title).replace(/"/g, '&quot;') + '">'
            : '';
          return '<div class="nf-rec">' +
                   '<a class="nf-rec__media" href="' + p.url + '">' + img + '</a>' +
                   '<div class="nf-rec__body">' +
                     '<a class="nf-rec__name" href="' + p.url + '">' + p.title + '</a>' +
                     '<span class="nf-rec__price">' + formatMoney(p.price) + '</span>' +
                   '</div>' +
                   '<button type="button" class="nf-btn nf-btn--outline" data-nf-rec-add="' +
                     p.variants[0].id + '">Add</button>' +
                 '</div>';
        }).join('');
        slot.innerHTML = html;
        /* Reveal the "You may also like" heading only now that there is
           something under it — an empty label is never rendered. */
        var wrap = slot.closest('[data-nf-recs-wrap]');
        if (wrap) wrap.removeAttribute('hidden');
        each($$('[data-nf-rec-add]', slot), function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            btn.disabled = true;
            addToCart(btn.getAttribute('data-nf-rec-add'), 1);
          });
        });
      })
      .catch(function () { /* keep the curated fallback already rendered */ });
  }

  /* ------------------------------------------------------------------ *
   * 4. Shop dropdown
   * ------------------------------------------------------------------ */

  function initDropdown(scope) {
    each($$('[data-nf-drop-toggle]', scope || document), function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;

      function close() {
        panel.setAttribute('data-open', 'false');
        btn.setAttribute('aria-expanded', 'false');
      }
      function open() {
        panel.setAttribute('data-open', 'true');
        btn.setAttribute('aria-expanded', 'true');
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        panel.getAttribute('data-open') === 'true' ? close() : open();
      });

      /* No mouseleave-close. The panel sits 14px below the button, so leaving
         the button to reach a tile fired mouseleave and shut the menu before it
         could be clicked. Escape, an outside click, or the button close it. */

      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
      document.addEventListener('click', function (e) {
        if (!panel.contains(e.target) && !btn.contains(e.target)) close();
      });
      close();
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. Mobile buy bar — hidden while the drawer or menu is open
   * ------------------------------------------------------------------ */

  /* Only ever mutate when the state actually changes. Writing `hidden` again
     when it is already set still produces a MutationRecord, which — with the
     observer below watching `hidden` — would feed straight back into this
     function and lock the renderer in an infinite mutation loop. */
  function hideBuyBar(hide) {
    var bar = $('[data-nf-buybar]');
    if (!bar) return;
    var already = bar.hasAttribute('hidden');
    if (hide === already) return;
    hide ? bar.setAttribute('hidden', '') : bar.removeAttribute('hidden');
  }

  function initBuyBar() {
    var bar = $('[data-nf-buybar]');
    if (!bar) return;

    /* The bar exists to put the button back within reach AFTER the real one has
       gone. While the in-panel button is still on screen — or still below the
       fold, further down the page — a floating duplicate adds nothing and just
       covers content. So it appears only once that button has passed off the
       TOP of the viewport, and goes away again on the way back up.

       `bottom < 0` is what separates "scrolled past it" from "haven't reached
       it yet" — the element is outside the viewport in both cases, so its side
       is the only thing that distinguishes them.

       This is a scroll listener rather than an IntersectionObserver on purpose.
       IO only reports when the intersection STATE changes, and both of those
       cases are "not intersecting" — so a jump-scroll from below the button to
       above it (anchor link, restored scroll position, back-to-top) never fires
       a callback at all and the bar gets stuck. Measuring one element's rect on
       scroll is cheap and cannot miss.

       Scoped to .nf-buy on purpose: a document-wide lookup would happily match
       the floating bar's OWN button — it carries the same hooks — and the bar
       would end up watching itself. */
    var panel = $('.nf-buy');
    var cta = panel ? $('[data-nf-add-btn], button[type="submit"]', panel) : null;
    var pastCta = !cta;

    function check() {
      hideBuyBar(!pastCta || !!$('[data-nf-overlay]:not([hidden])'));
    }

    function measure() {
      if (!cta) return;
      var next = cta.getBoundingClientRect().bottom < 0;
      if (next === pastCta) return;
      pastCta = next;
      check();
    }

    if (cta) {
      window.addEventListener('scroll', measure, { passive: true });
      window.addEventListener('resize', measure, { passive: true });
    }

    /* Overlays are opened and closed by nefer-motion.js, which toggles
       [hidden]. Mutations coming from the bar itself are ignored — belt and
       braces alongside the no-op guard above. */
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].target !== bar) { check(); return; }
      }
    }).observe(document.body, {
      subtree: true, attributes: true, attributeFilter: ['hidden']
    });

    check();
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  function boot() {
    initVariants();
    initQty();
    initAddForms();
    initDropdown();
    bindDrawer();
    initBuyBar();
    syncCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initVariants(e.target);
    initQty(e.target);
    initAddForms(e.target);
    initDropdown(e.target);
    bindDrawer();
  });

  window.NeferShop = {
    add: addToCart,
    addItems: addItems,
    change: changeLine,
    formatMoney: formatMoney,
    refresh: boot
  };
})();
